import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ArrowRight, Heart, Menu, X } from 'lucide-react'

import { donationUrl, navItems } from '@/data/site'

import { ButtonLink } from './ButtonLink'

const secondaryLinks = [
  { label: 'Ruta crítica', href: '/ruta-critica/' },
  { label: 'Los Tíos Chidos', href: '/los-tios-chidos/' },
]

const panelTransition = { duration: 0.78, ease: [0.22, 1, 0.36, 1] }

export function Header({ currentPage }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    document.body.classList.toggle('menu-open', menuOpen)
    const backgroundElements = [
      document.querySelector('main'),
      document.querySelector('footer'),
    ].filter(Boolean)

    for (const element of backgroundElements) {
      element.inert = menuOpen

      if (menuOpen) {
        element.setAttribute('aria-hidden', 'true')
      } else {
        element.removeAttribute('aria-hidden')
      }
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
      }
    }

    window.addEventListener('keydown', closeOnEscape)

    return () => {
      document.body.classList.remove('menu-open')
      window.removeEventListener('keydown', closeOnEscape)

      for (const element of backgroundElements) {
        element.inert = false
        element.removeAttribute('aria-hidden')
      }
    }
  }, [menuOpen])

  return (
    <>
      <header className={`site-header ${menuOpen ? 'menu-is-open' : ''}`}>
        <div className="container-wide header-inner">
          <a
            className="brand-link"
            href="/"
            aria-label="The Noa Movement, ir al inicio"
          >
            <img
              className="brand-logo"
              src="/assets/brand/logo.webp"
              alt=""
              width="466"
              height="202"
            />
          </a>

          <div className="header-actions">
            <a
              className="button button-primary header-donate"
              href={donationUrl}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Donar ahora"
            >
              <span>Donar ahora</span>
              <Heart aria-hidden="true" size={18} strokeWidth={2.4} />
            </a>
            <button
              className="menu-toggle"
              type="button"
              aria-controls="site-menu"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="menu-toggle-label">
                {menuOpen ? 'Cerrar' : 'Menú'}
              </span>
              <span className="menu-toggle-icon">
                {menuOpen ? (
                  <X aria-hidden="true" size={21} />
                ) : (
                  <Menu aria-hidden="true" size={21} />
                )}
              </span>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            className="menu-panel"
            id="site-menu"
            initial={reduceMotion ? false : { y: '-102%' }}
            animate={{ y: 0 }}
            exit={reduceMotion ? undefined : { y: '-102%' }}
            transition={panelTransition}
          >
            <div className="menu-orb menu-orb-one" aria-hidden="true" />
            <div className="menu-orb menu-orb-two" aria-hidden="true" />

            <div className="container-wide menu-panel-inner">
              <div className="menu-panel-heading">
                <p>Explora el movimiento</p>
                <span>Recursos · comunidad · acción</span>
              </div>

              <div className="menu-layout">
                <nav className="menu-nav" aria-label="Navegación principal">
                  {navItems.map((item, index) => (
                    <motion.a
                      className="menu-link"
                      href={item.href}
                      initial={
                        reduceMotion ? false : { opacity: 0, y: 28, rotateX: -10 }
                      }
                      animate={{ opacity: 1, y: 0, rotateX: 0 }}
                      transition={{
                        duration: 0.65,
                        delay: 0.18 + index * 0.055,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      aria-current={currentPage === item.page ? 'page' : undefined}
                      key={item.href}
                    >
                      <span className="menu-link-number">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="menu-link-label">{item.label}</span>
                      <span className="menu-link-arrow">
                        <ArrowRight aria-hidden="true" size={24} />
                      </span>
                    </motion.a>
                  ))}
                </nav>

                <motion.aside
                  className="menu-aside"
                  initial={reduceMotion ? false : { opacity: 0, x: 35 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.75, delay: 0.3 }}
                >
                  <div className="menu-feature">
                    <img
                      src="/assets/images/noa-mirada.webp"
                      alt="Noa mirando hacia arriba"
                      width="1076"
                      height="1308"
                    />
                    <span>Una historia que mueve</span>
                  </div>

                  <p className="menu-manifesto">
                    Que ninguna familia tenga que encontrar respuestas sola.
                  </p>

                  <ButtonLink
                    className="menu-donate"
                    external
                    href={donationUrl}
                    variant="primary"
                  >
                    Donar ahora
                  </ButtonLink>

                  <div className="menu-secondary">
                    {secondaryLinks.map((item) => (
                      <a href={item.href} key={item.href}>
                        {item.label}
                        <ArrowRight aria-hidden="true" size={15} />
                      </a>
                    ))}
                  </div>
                </motion.aside>
              </div>

              <div className="menu-panel-footer">
                <span>The Noa Movement®</span>
                <span>La esperanza es obligatoria</span>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
}
